import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  type AgeUpdateSettings,
  GetWorkspaceAgeSettingsDocument,
  UpdateIssueDocument,
  UpdateWorkspaceDocument,
} from "#app/lib/data";

// `AgeUpdateSettings` is a row alias over the fragment on the schema type of
// the same name. `use-update-age-dialog` hands out this same alias.
export type { AgeUpdateSettings };

const DEFAULT_SETTINGS: AgeUpdateSettings = {
  __typename: "AgeUpdateSettings",
  reasonsEnabled: false,
  requireReason: false,
  requireComment: false,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUpdatesSettings() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const queryVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data, loading, error } = useQuery(GetWorkspaceAgeSettingsDocument, {
    variables: queryVars,
    skip: !workspaceId,
    fetchPolicy: "cache-and-network",
  });

  const [updateWorkspaceMutation] = useMutation(UpdateWorkspaceDocument);
  const [updateIssueMutation] = useMutation(UpdateIssueDocument);

  const settings = useMemo<AgeUpdateSettings>(
    () => data?.workspace?.ageUpdateSettings ?? DEFAULT_SETTINGS,
    [data],
  );

  const updateSetting = useCallback(
    async (field: keyof AgeUpdateSettings, value: boolean) => {
      if (!workspaceId) return;
      try {
        await updateWorkspaceMutation({
          variables: {
            id: workspaceId,
            input: {
              ageUpdateSettings: {
                reasonsEnabled: settings.reasonsEnabled,
                requireReason: settings.requireReason,
                requireComment: settings.requireComment,
                [field]: value,
              },
            },
          },
        });
        toastManager.add({ title: "Setting updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update setting");
      }
    },
    [workspaceId, settings, updateWorkspaceMutation],
  );

  const updateIssueAge = useCallback(
    async (issueId: string, input: { status: string; note?: string; reasonId?: string | null }) => {
      await updateIssueMutation({
        variables: {
          issueId,
          input: {
            age: {
              status: input.status,
              note: input.note,
              reasonId: settings.reasonsEnabled ? input.reasonId : undefined,
            },
          },
        },
      });
    },
    [updateIssueMutation, settings.reasonsEnabled],
  );

  return {
    settings,
    hasData: !!data?.workspace,
    isLoading: loading,
    error,
    updateSetting,
    updateIssueAge,
  };
}
